import { useEffect, useState } from "react";
import Cropper, { Area } from "react-easy-crop";
import { Box, Button, Dialog, Slider, Typography } from "@mui/material";
import { makeSxStyles } from "ui";
import useObjectUrl from "@/shared/hooks/useObjectUrl";

// Keeps the exported file well under the upload size limit.
const MAX_OUTPUT_WIDTH = 2400;
const JPEG_QUALITY = 0.9;
// react-easy-crop zooms by pixelY * zoomSpeed / 200 per wheel event, and a
// mouse wheel notch is ~100px. With the default speed one notch moves 0.5,
// which spans the whole range when maxZoom is small.
const WHEEL_NOTCHES_TO_MAX_ZOOM = 10;

interface ImageCropDialogProps {
  /** The image to crop. The dialog is open while this is set. */
  file: File | null;
  /** Width / height ratio of the crop frame, e.g. 3 for 3:1. */
  aspectRatio: number;
  aspectRatioLabel?: string;
  /** The crop can't be zoomed in past this width (in image pixels). */
  minWidth: number;
  onApply: (file: File) => void;
  onCancel: () => void;
}

const loadImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Unreadable image"));
    image.src = url;
  });

// Draws the selected area onto a canvas at an exact width / height ratio, so
// the backend's ratio check always passes.
const cropToFile = async (
  file: File,
  url: string,
  area: Area,
  aspectRatio: number,
  minWidth: number,
) => {
  const image = await loadImage(url);
  // The API checks width === height * ratio exactly, so derive the width from
  // a whole-pixel height. Clamping to minWidth covers a frame that rounds a
  // pixel short at full zoom.
  const targetWidth = Math.min(
    Math.max(area.width, minWidth),
    MAX_OUTPUT_WIDTH,
  );
  const height = Math.round(targetWidth / aspectRatio);
  const width = height * aspectRatio;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas not supported");
  // JPEG has no transparency - give transparent PNGs a white backdrop.
  context.fillStyle = "#fff";
  context.fillRect(0, 0, width, height);
  context.drawImage(
    image,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    width,
    height,
  );

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
  );
  if (!blob) throw new Error("Could not export the image");
  const baseName = file.name.replace(/\.[^.]+$/, "");
  return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
};

// Lets the user drag and zoom an image inside a fixed-ratio frame, then
// returns the framed part as a new file (ESR-1656).
const ImageCropDialog = ({
  file,
  aspectRatio,
  aspectRatioLabel,
  minWidth,
  onApply,
  onCancel,
}: ImageCropDialogProps) => {
  const styles = useStyles();
  const url = useObjectUrl(file);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [maxZoom, setMaxZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setMaxZoom(1);
    setArea(null);
    setError(null);
  }, [file]);

  const handleApply = async () => {
    if (!file || !url || !area) return;
    setIsApplying(true);
    try {
      onApply(await cropToFile(file, url, area, aspectRatio, minWidth));
    } catch {
      setError("Couldn't crop this image. Try another file.");
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <Dialog open={Boolean(file)} onClose={onCancel} maxWidth="md" fullWidth>
      <Box sx={styles.content}>
        <Typography sx={styles.title}>Crop image</Typography>
        <Typography sx={styles.description}>
          Drag and zoom to choose the part shown in the{" "}
          {aspectRatioLabel ?? `${aspectRatio}:1`} banner.
        </Typography>
        <Box sx={styles.cropArea}>
          {url && (
            <Cropper
              image={url}
              crop={crop}
              zoom={zoom}
              maxZoom={maxZoom}
              zoomSpeed={((maxZoom - 1) * 2) / WHEEL_NOTCHES_TO_MAX_ZOOM}
              aspect={aspectRatio}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, croppedAreaPixels) =>
                setArea(croppedAreaPixels)
              }
              onMediaLoaded={({ naturalWidth, naturalHeight }) => {
                // At zoom 1 the frame spans the image's limiting side. Stop
                // zooming once the frame would be narrower than minWidth.
                const fullCropWidth = Math.min(
                  naturalWidth,
                  naturalHeight * aspectRatio,
                );
                setMaxZoom(Math.max(1, fullCropWidth / minWidth));
              }}
            />
          )}
        </Box>
        <Slider
          value={zoom}
          min={1}
          max={maxZoom}
          step={0.01}
          disabled={maxZoom <= 1}
          onChange={(_, value) => setZoom(value as number)}
          aria-label="Zoom"
        />
        {error && <Typography sx={styles.error}>{error}</Typography>}
      </Box>
      <Box sx={styles.actions}>
        <Button onClick={onCancel} sx={styles.cancelButton}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleApply()}
          disabled={!area || isApplying}
          sx={styles.applyButton}
          data-testid="image-crop-apply"
        >
          Apply
        </Button>
      </Box>
    </Dialog>
  );
};

const useStyles = makeSxStyles(({ palette: { primary, grey, error } }) => ({
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    pb: "24px",
  },
  title: {
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: "22px",
    lineHeight: "28px",
    color: grey[100],
  },
  description: {
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "20px",
    color: grey[200],
  },
  cropArea: {
    position: "relative",
    height: "360px",
    borderRadius: "8px",
    overflow: "hidden",
    background: "#000",
  },
  error: {
    fontSize: "14px",
    lineHeight: "20px",
    color: error.main,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
  },
  cancelButton: {
    color: primary.main,
    textTransform: "none",
    borderRadius: "100px",
    padding: "10px 12px",
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: "14px",
  },
  applyButton: {
    background: primary.main,
    color: primary.contrastText,
    textTransform: "none",
    borderRadius: "100px",
    padding: "10px 24px",
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: "14px",
    "&:hover": { background: primary.main },
    "&.Mui-disabled": {
      background: primary.main,
      color: primary.contrastText,
      opacity: 0.7,
    },
  },
}));

export default ImageCropDialog;
